import { useEffect, useRef, useState } from "react";
import { DeliveryStatus, FinancialStatus } from "@hybrid-escrow/shared";
import type { OrderResponse } from "@hybrid-escrow/shared";
import { CountdownTimer } from "./CountdownTimer";

/**
 * A visual lifecycle stepper for the one place this app has a genuine, honest "unfinished
 * process" — an escrow order. Deliberately calm rather than gamified: this is money in flight,
 * so the only animation is a brief settle-in when a step completes while the viewer is watching
 * (see `justSettled` below), never anything bouncy/celebratory. The four steps are the same for
 * every order; a dispute is rendered as an interruption of step 3, not a fifth step, since it's
 * a branch off the happy path rather than a stage everyone passes through.
 */

type StepState = "done" | "current" | "pending" | "alert";

interface Step {
  key: string;
  label: string;
  sublabel?: string;
  state: StepState;
}

function computeSteps(order: OrderResponse): Step[] {
  const funded =
    order.financialStatus !== FinancialStatus.PENDING_FUNDING &&
    order.financialStatus !== FinancialStatus.EXPIRED &&
    order.financialStatus !== FinancialStatus.FAILED;
  // ACCOUNT_TRANSFER, DOMAIN_TRANSFER, and NFT_TRANSFER never touch deliveryStatus at all (see
  // services/accountTransfers.ts, services/domainTransfers.ts, services/nftTransfers.ts) — their
  // own stage fields are the real signal for whether the buyer has something in hand.
  const delivered =
    order.listingFulfillmentType === "ACCOUNT_TRANSFER"
      ? order.accountTransferStage === "READY_FOR_BUYER" ||
        order.accountTransferStage === "CREDENTIALS_REVEALED"
      : order.listingFulfillmentType === "DOMAIN_TRANSFER"
        ? order.domainTransferStage === "READY_FOR_BUYER" ||
          order.domainTransferStage === "CODE_REVEALED"
        : order.listingFulfillmentType === "NFT_TRANSFER"
          ? order.nftTransferStage === "DELIVERED"
          : order.deliveryStatus === DeliveryStatus.AVAILABLE ||
            order.deliveryStatus === DeliveryStatus.DOWNLOADED;
  const disputed = order.financialStatus === FinancialStatus.DISPUTED;
  const settled =
    order.financialStatus === FinancialStatus.RELEASED ||
    order.financialStatus === FinancialStatus.REFUNDED;

  const fundedState: StepState = funded ? "done" : "current";
  const deliveredState: StepState = !funded ? "pending" : delivered ? "done" : "current";
  const disputeWindowState: StepState = !funded
    ? "pending"
    : disputed
      ? "alert"
      : settled
        ? "done"
        : "current";
  const settledState: StepState = settled ? "done" : disputed ? "current" : "pending";

  return [
    { key: "funded", label: "Payment funded", state: fundedState },
    {
      key: "delivered",
      label:
        order.listingFulfillmentType === "ACCOUNT_TRANSFER"
          ? "Account handoff"
          : order.listingFulfillmentType === "DOMAIN_TRANSFER"
            ? "Domain handoff"
            : order.listingFulfillmentType === "NFT_TRANSFER"
              ? "NFT handoff"
              : "Delivery",
      state: deliveredState,
    },
    {
      key: "dispute-window",
      label: disputed ? "Dispute opened" : "Dispute window",
      sublabel:
        disputeWindowState === "current" && order.disputeDeadline ? "Closes in" : undefined,
      state: disputeWindowState,
    },
    {
      key: "settled",
      label:
        order.financialStatus === FinancialStatus.REFUNDED
          ? "Refunded to buyer"
          : disputed
            ? "Awaiting resolution"
            : "Released",
      state: settledState,
    },
  ];
}

export function OrderProgressStepper({ order }: { order: OrderResponse }) {
  const prevKeyRef = useRef<string | null>(null);
  const [justSettledKey, setJustSettledKey] = useState<string | null>(null);
  const steps = computeSteps(order);

  // A step that flips to "done" while this component is already mounted is a step the viewer is
  // actually watching complete — that's the only case worth a settle-in flourish. A step that's
  // already "done" on first render (the page loaded after the fact) gets no animation, since
  // there's nothing to celebrate about a fact that was already true.
  useEffect(() => {
    const doneKeys = steps.filter((s) => s.state === "done").map((s) => s.key);
    const latestDone = doneKeys[doneKeys.length - 1] ?? null;
    if (prevKeyRef.current !== null && latestDone !== prevKeyRef.current && latestDone) {
      setJustSettledKey(latestDone);
      const t = setTimeout(() => setJustSettledKey(null), 700);
      return () => clearTimeout(t);
    }
    prevKeyRef.current = latestDone;
    return undefined;
    // Deliberately keyed on the raw fields that actually drive step state, not `steps` itself (a
    // fresh array every render) — that would fire on every render instead of only on a genuine
    // status change. accountTransferStage/domainTransferStage are what move the "delivered" step
    // for those fulfillment types, since deliveryStatus never changes for either (see the
    // `delivered` computation above).
  }, [
    order.financialStatus,
    order.deliveryStatus,
    order.accountTransferStage,
    order.domainTransferStage,
    order.nftTransferStage,
  ]);

  return (
    <div className="order-stepper" role="list" aria-label="Order progress">
      {steps.map((step, i) => (
        <div
          key={step.key}
          className={`order-stepper__step order-stepper__step--${step.state}`}
          role="listitem"
        >
          <div className="row" style={{ width: "100%", alignItems: "center", gap: 0 }}>
            {i > 0 && (
              <div
                className={`order-stepper__connector ${
                  steps[i - 1]?.state === "done" ? "order-stepper__connector--done" : ""
                }`}
                aria-hidden="true"
              />
            )}
            <div
              className={[
                "order-stepper__dot",
                step.state === "done" ? "order-stepper__dot--done" : "",
                step.state === "current" ? "order-stepper__dot--current order-stepper__dot--pulse" : "",
                step.state === "alert" ? "order-stepper__dot--alert" : "",
                justSettledKey === step.key ? "order-stepper__dot--settling" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {step.state === "done" ? "✓" : step.state === "alert" ? "!" : i + 1}
            </div>
            {i < steps.length - 1 && (
              <div
                className={`order-stepper__connector ${
                  step.state === "done" ? "order-stepper__connector--done" : ""
                }`}
                aria-hidden="true"
              />
            )}
          </div>
          <span className="order-stepper__label">{step.label}</span>
          {step.sublabel && order.disputeDeadline && (
            <span className="order-stepper__sublabel">
              <CountdownTimer deadlineIso={order.disputeDeadline} />
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
